import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Typography, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";

import { uploadToPresignedUrl } from "./api";

const { Dragger } = Upload;
const { Text } = Typography;
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

const InternalDriveUploader = ({ api, path, onComplete }) => {
  const [fileList, setFileList] = useState([]);
  const [conflict, setConflict] = useState({
    open: false,
    filename: "",
    draftFilename: "",
  });
  const conflictResolver = useRef(null);
  const uploadQueue = useRef(Promise.resolve());

  useEffect(
    () => () => {
      if (conflictResolver.current) {
        conflictResolver.current({ action: "cancel" });
      }
    },
    [],
  );

  const closeConflict = (result) => {
    conflictResolver.current?.(result);
    conflictResolver.current = null;
    setConflict({ open: false, filename: "", draftFilename: "" });
  };

  const askConflictResolution = (filename) =>
    new Promise((resolve) => {
      conflictResolver.current = resolve;
      setConflict({
        open: true,
        filename,
        draftFilename: filename,
      });
    });

  const resolveUploadTarget = async (file) => {
    let filename = file.name;
    let overwrite = false;

    while (true) {
      try {
        const target = await api.createUpload({
          path,
          filename,
          contentType: file.type || "application/octet-stream",
          contentLength: file.size,
          overwrite,
        });
        return { ...target, filename, overwrite };
      } catch (error) {
        if (error.status !== 409 || error.code !== "ITEM_ALREADY_EXISTS") {
          throw error;
        }

        const resolution = await askConflictResolution(filename);
        if (resolution.action === "cancel") {
          const cancelled = new Error("Upload annullato");
          cancelled.code = "UPLOAD_CANCELLED";
          throw cancelled;
        }
        if (resolution.action === "overwrite") {
          overwrite = true;
        } else {
          filename = resolution.filename;
          overwrite = false;
        }
      }
    }
  };

  const customRequest = (options) => {
    const runUpload = async () => {
      const { file, onError, onProgress, onSuccess } = options;
      const rawFile = file.originFileObj || file;

      try {
        const target = await resolveUploadTarget(rawFile);
        await uploadToPresignedUrl({
          file: rawFile,
          headers: target.headers,
          method: target.method,
          onProgress,
          url: target.url,
        });
        onSuccess({ key: target.key }, rawFile);
        message.success(`Caricato: ${target.filename}`);
        onComplete?.(target.key);
      } catch (error) {
        if (error.code === "UPLOAD_CANCELLED") {
          message.info("Upload annullato");
        } else {
          message.error(error.message || "Upload non riuscito");
        }
        onError(error);
      }
    };

    const queued = uploadQueue.current.then(runUpload);
    uploadQueue.current = queued.catch(() => undefined);
    return { abort() {} };
  };

  const beforeUpload = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      message.error("Il singolo file non può superare 5 GB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const confirmRename = () => {
    const filename = conflict.draftFilename.trim();
    if (!filename) {
      message.error("Inserisci un nome valido");
      return;
    }
    closeConflict({ action: "rename", filename });
  };

  return (
    <>
      <Dragger
        multiple
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        fileList={fileList}
        onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
        onRemove={(file) => {
          setFileList((current) =>
            current.filter((item) => item.uid !== file.uid),
          );
          return true;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Trascina file qui oppure clicca per selezionarli
        </p>
        <p className="ant-upload-hint">
          Tutti i formati sono supportati. Massimo 5 GB per file.
        </p>
      </Dragger>

      <Modal
        open={conflict.open}
        title="Elemento già presente"
        onCancel={() => closeConflict({ action: "cancel" })}
        footer={[
          <Button
            key="cancel"
            onClick={() => closeConflict({ action: "cancel" })}
          >
            Annulla
          </Button>,
          <Button
            key="overwrite"
            danger
            onClick={() => closeConflict({ action: "overwrite" })}
          >
            Sovrascrivi
          </Button>,
          <Button key="rename" type="primary" onClick={confirmRename}>
            Rinomina e carica
          </Button>,
        ]}
      >
        <p>
          Esiste già un elemento chiamato{" "}
          <Text strong>{conflict.filename}</Text>.
        </p>
        <Input
          autoFocus
          value={conflict.draftFilename}
          onChange={(event) =>
            setConflict((current) => ({
              ...current,
              draftFilename: event.target.value,
            }))
          }
          onPressEnter={confirmRename}
        />
      </Modal>
    </>
  );
};

export default InternalDriveUploader;
